/**
 * @license Copyright (c) Microsoft Corporation. All rights reserved.
 */
/**
 * An object that encapsulates a Guid value
 */
export class Guid {
    get guid() {
        return this._guid;
    }
    /**
     * This empty setter will prevent that TS emits 'readonly' modifier.
     * 'readonly' is not supported in current stable release.
    */
    set guid(value) { }
    /**
     * The empty guid
     */
    static get empty() {
        return new Guid("00000000-0000-0000-0000-000000000000");
    }
    /**
     * Generates a new V4 GUID string.
     * @return {string} GUIDv4 string generated using `random()`.
     */
    static get new() {
        return new Guid("xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
            const r = (Math.random() * 16) | 0, v = c === "x" ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        }));
    }
    /**
     * Creates a new Guid instance.
     * @param value The string value to create as a Guid
     */
    constructor(value) {
        this._guid = Guid._formatGuidString(value);
        if (!this.guid) {
            throw new Error(`${value} is not a valid Guid value.`);
        }
        Object.freeze(this);
    }
    /**
     * Output a properly formatted string value for the Guid
     * @param guid The guid
     * @returns The string representation of the Guid
     */
    static toString(guid) {
        if (guid) {
            return guid.guid;
        }
        return null;
    }
    /**
     * Output a properly formatted string value for this Guid instance
     * Added because toString is called in EntityReference.fromLegacyEntityReference -> Guid.fromLegacyGuid in UCI
     * Otherwise, toString will return [object Object]
     * @returns The string representation of this Guid
     */
    toString() {
        return this.guid;
    }
    /**
     * Determine if two guids are the same
     * @param x A guid
     * @param y A guid
     * @returns Whether the two guids are equal
     */
    static equals(x, y) {
        // if the canonical string representations are the same, the guids are equal
        return Guid.toString(x) === Guid.toString(y);
    }
    /**
     * Attempt to create a Guid from a string that may or may not represent a Guid
     * @param value The string with a Guid value
     * @returns A valid Guid, or the empty Guid
     */
    static tryParse(value) {
        return Guid.tryParseOrNull(value) || Guid.empty;
    }
    /**
     * Attempt to create a Guid from a string that may or may not represent a Guid
     * @param value The string with a Guid value
     * @returns A valid Guid, or null
     */
    static tryParseOrNull(value) {
        const parsedValue = Guid._formatGuidString(value);
        if (parsedValue) {
            return new Guid(parsedValue);
        }
        else {
            return null;
        }
    }
    /**
     * Format the guid string in the default Guid string format
     * @param value The string with a Guid value
     * @returns The formatted Guid string
     */
    static _formatGuidString(value) {
        if (!value) {
            return null;
        }
        // Normalize to lower case
        value = value.toLowerCase();
        // Validate and update Guid string
        const hyphenMatch = Guid.braceAndHyphenGuidVerifierPattern.exec(value);
        if (hyphenMatch) {
            // verify that either both braces were specified or neither
            if (hyphenMatch[1].length !== hyphenMatch[8].length) {
                return null;
            }
            // extract the body of the Guid
            return hyphenMatch[2];
        }
        else {
            const contiguousMatch = Guid.contiguousGuidVerifierPattern.exec(value);
            if (contiguousMatch) {
                // join the matched groups with hyphens
                return contiguousMatch.filter((_, index) => index > 0).join("-");
            }
        }
        return null;
    }
}
/**
 * Regular expression for validating a guid string consisting of groups of 8, 4, 4, 4, and 12
 * hexadecimal digits with hyphens between the groups and enclosed by optional braces
 * Examples: {12345678-1234-1234-1234-123456789abc}, 12345678-1234-1234-1234-123456789abc
 */
Guid.braceAndHyphenGuidVerifierPattern = /^({?)((\d|[a-f]){8}-(\d|[a-f]){4}-(\d|[a-f]){4}-(\d|[a-f]){4}-(\d|[a-f]){12})(}?)$/;
/**
 * Regular expression for validating a guid string consisting of 32 contiguous hex digits, capturing them in groups of 8, 4, 4, 4, and 12
 * Example: 12345678123412341234123456789abc
 */
Guid.contiguousGuidVerifierPattern = /^([a-f\d]{8})([a-f\d]{4})([a-f\d]{4})([a-f\d]{4})([a-f\d]{12})$/;
//# sourceMappingURL=Guid.js.map